<template>
  <b-modal
    v-model="localModal"
    :title="isEdit ? 'Edit Error Description' : 'Create Error Description'"
    @hidden="resetForm"
    ok-variant="primary"
    :ok-title="isEdit ? 'Update' : 'Create'"
    cancel-title="Cancel"
    :ok-disabled="isLoading"
    size="lg"
    centered
    body-class="p-0"
  >
    <b-form ref="form">
      <b-card class="border-0">
        <b-row>
          <!-- Code -->
          <b-col sm="12" md="6" class="mb-3">
            <label class="required" for="code">Code</label>
            <b-form-input
              id="code"
              v-model="form.code"
              placeholder="Enter code (e.g., 400-wo-001)"
              :state="$v.form.code.$dirty ? !$v.form.code.$error : null"
              @input="$v.form.code.$touch()"
            />
            <b-form-invalid-feedback :state="$v.form.code.$dirty ? !$v.form.code.$error : null">
              Code is required
            </b-form-invalid-feedback>
          </b-col>

          <!-- Type -->
          <b-col sm="12" md="6" class="mb-3">
            <label class="required" for="type">Type</label>
            <multiselect
              id="type"
              v-model="form.type"
              :options="typeOptions"
              :multiple="false"
              :close-on-select="true"
              :clear-on-select="false"
              :hide-selected="true"
              :allow-empty="false"
              placeholder="Select type"
              label="name"
              track-by="id"
              :class="{ 'is-invalid': $v.form.type.$dirty && $v.form.type.$error }"
              @input="$v.form.type.$touch()"
            />
            <b-form-invalid-feedback :state="$v.form.type.$dirty ? !$v.form.type.$error : null">
              Type is required
            </b-form-invalid-feedback>
          </b-col>

          <!-- Header (optional) -->
          <b-col sm="12" md="6" class="mb-3">
            <label class="required" for="header">Header</label>
            <b-form-input id="header" v-model="form.header" placeholder="Enter header" :class="{ 'is-invalid': $v.form.header.$dirty && $v.form.header.$error }" @input="$v.form.header.$touch()" />
            <b-form-invalid-feedback :state="$v.form.header.$dirty ? !$v.form.header.$error : null">
              Header is required
            </b-form-invalid-feedback>
          </b-col>

          <!-- Sub Header (optional) -->
          <b-col sm="12" md="6" class="mb-3">
            <label class="required" for="sub_header">Sub Header</label>
            <b-form-input
              id="sub_header"
              v-model="form.sub_header"
              placeholder="Enter sub header"
              :class="{ 'is-invalid': $v.form.sub_header.$dirty && $v.form.sub_header.$error }"
              @input="$v.form.sub_header.$touch()"
            />
            <b-form-invalid-feedback :state="$v.form.sub_header.$dirty ? !$v.form.sub_header.$error : null">
              Sub Header is required
            </b-form-invalid-feedback>
          </b-col>

          <!-- Body (WAJIB) -->
          <b-col sm="12" class="mb-3">
            <label class="required" for="body">Body</label>
            <vue-editor
              v-if="localModal"
              id="body"
              v-model="form.body"
              placeholder="Enter body message"
              :class="{ 'is-invalid': $v.form.body.$dirty && $v.form.body.$error }"
              @input="$v.form.body.$touch()"
            />
            <b-form-invalid-feedback :state="$v.form.body.$dirty ? !$v.form.body.$error : null">
              Body is required
            </b-form-invalid-feedback>
          </b-col>

          <!-- Footer (optional) -->
          <b-col sm="12" class="mb-3">
            <label for="footer">Footer</label>
            <b-form-input id="footer" v-model="form.footer" placeholder="Enter footer" />
          </b-col>
        </b-row>
      </b-card>
    </b-form>

    <template #modal-footer="{ cancel }">
      <b-row class="w-100 justify-content-end">
        <b-col cols="auto">
          <b-button variant="secondary" @click="cancel" :disabled="isLoading" class="me-2">
            Cancel
          </b-button>
          <b-button variant="primary" @click="handleSubmit" :disabled="isLoading">
            <b-spinner v-if="isLoading" small class="me-1"></b-spinner>
            {{ isEdit ? 'Update' : 'Create' }}
          </b-button>
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import { VueEditor } from 'vue2-editor'
import Multiselect from 'vue-multiselect'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'FormErrorDescription',
  components: {
    VueEditor,
    Multiselect,
  },
  props: {
    modal: {
      type: Boolean,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    initialData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      form: {
        code: '',
        header: '',
        sub_header: '',
        body: '',
        footer: '',
        type: null,
      },
      typeOptions: [
        { id: 'info', name: 'Info' },
        { id: 'warn', name: 'Warn' },
        { id: 'error', name: 'Error' },
      ],
      localModal: this.modal,
      editorKey: 0,
    }
  },
  validations: {
    form: {
      code: { required },
      type: { required },
      body: { required },
      header: { required },
      sub_header: { required },
    },
  },
  computed: {
    isEdit() {
      return !!this.initialData && !!this.initialData.code
    },
  },
  watch: {
    initialData: {
      immediate: true,
      handler(val) {
        if (val) {
          // Cari object type berdasarkan id
          const typeObject = this.typeOptions.find((opt) => opt.id === val.type) || null
          // Ambil subheader (API pakai "subheader", form pakai "sub_header")
          const subHeaderValue = val.sub_header || ''
          this.form = {
            code: val.code || '',
            header: val.header || '',
            sub_header: subHeaderValue,
            body: val.body || '',
            footer: val.footer || '',
            type: typeObject,
          }
        } else {
          this.resetForm()
        }
        this.$v.form.$reset()
      },
    },
    modal(val) {
      this.localModal = val
    },
    localModal(val) {
      // Jika modal ditutup (false), beri tahu induk
      if (!val) {
        this.$emit('update:modal', false)
      }
    },
  },
  methods: {
    resetForm() {
      this.form = {
        code: '',
        header: '',
        sub_header: '',
        body: '',
        footer: '',
        type: null,
      }
      this.$v.form.$reset()
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    },
    handleSubmit() {
      this.$v.form.$touch()
      if (this.$v.form.$anyError) {
        this.$mcToast.error('Please fill all required fields')
        return
      }
      const payload = { ...this.form }
      // Konversi type object ke ID string
      payload.type = payload.type?.id || null
      if (this.isEdit) {
        payload._edit = true
      }
      this.localModal = false
      this.$emit('submit', payload)
    },
    closeModal() {
      this.localModal = false
    },
  },
}
</script>

<style scoped>
.b-modal .modal-body {
  padding-top: 0 !important;
}
.card.border-0 {
  margin-bottom: 0;
}
.required::after {
  content: ' *';
  color: red;
}
</style>
